import { api, errMsg } from "@/lib/api";

let items = [];
const subs = new Set();
const emit = () => { items = [...items]; subs.forEach((f) => f()); };
export const subscribeUploads = (f) => { subs.add(f); return () => subs.delete(f); };
export const getUploads = () => items;
const patch = (id, p) => { items = items.map((i) => (i.id === id ? { ...i, ...p } : i)); emit(); };
let workers = 0;
const PARALLEL = 3;

async function withRetry(fn, tries = 4) {
  for (let i = 0; ; i++) {
    try { return await fn(); } catch (e) {
      const s = e?.response?.status;
      if (i >= tries - 1 || (s && s < 500 && s !== 408 && s !== 429)) throw e;
      await new Promise((r) => setTimeout(r, 1500 * (i + 1)));
    }
  }
}

async function uploadOne(it) {
  const { file } = it;
  patch(it.id, { status: "uploading", progress: 0, error: null });
  const init = await withRetry(() => api.post("/uploads/init", { client_id: it.clientId, folder_id: it.folderId, name: file.name, size: file.size, content_type: file.type }));
  const { upload_id, chunk_size } = init.data;
  const total = Math.ceil(file.size / chunk_size);
  for (let i = 0; i < total; i++) {
    const blob = file.slice(i * chunk_size, Math.min(file.size, (i + 1) * chunk_size));
    await withRetry(() => api.put(`/uploads/${upload_id}/chunk`, blob, {
      params: { index: i }, headers: { "Content-Type": "application/octet-stream" }, timeout: 120000,
      onUploadProgress: (e) => patch(it.id, { progress: Math.min(99, Math.round(((i * chunk_size + (e.loaded || 0)) / file.size) * 100)) }),
    }));
    patch(it.id, { progress: Math.min(99, Math.round((((i + 1) * chunk_size) / file.size) * 100)) });
  }
  patch(it.id, { status: "finishing", progress: 99 });
  await withRetry(() => api.post(`/uploads/${upload_id}/complete`, null, { timeout: 300000 }), 2);
  patch(it.id, { status: "done", progress: 100 });
  window.dispatchEvent(new Event("drive-changed"));
}

async function worker() {
  workers++;
  let next;
  while ((next = items.find((i) => i.status === "queued"))) {
    patch(next.id, { status: "uploading" });
    try { await uploadOne(next); } catch (e) { patch(next.id, { status: "error", error: errMsg(e) }); }
  }
  workers--;
}

function run() {
  while (workers < PARALLEL && items.some((i) => i.status === "queued")) worker();
}

export function enqueueUploads(files, clientId, folderId) {
  const add = Array.from(files || []).map((file) => ({ id: `${Date.now()}-${Math.random().toString(36).slice(2)}`, file, name: file.name, size: file.size, clientId, folderId, status: "queued", progress: 0 }));
  items = [...items, ...add];
  emit();
  run();
}

export const retryUpload = (id) => { patch(id, { status: "queued", progress: 0, error: null }); run(); };
export const clearFinished = () => { items = items.filter((i) => i.status !== "done" && i.status !== "error"); emit(); };
